import { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { updateProfile } from 'firebase/auth'
import { doc, updateDoc } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { useAuth } from '../hooks/useAuth'
import { db, storage } from '@/config/firebase'
import { Button } from '@/components/Button'
import { SimpleNotificationSettings } from '@/components/SimpleNotificationSettings'
import { ThemeSettings } from '@/components/ThemeToggle'
import toast from 'react-hot-toast'
import { useTranslation } from '@/i18n'

export function ProfilePage() {
  const { user } = useAuth()
  const { t } = useTranslation()
  const [displayName, setDisplayName] = useState(user?.displayName || '')
  const [loading, setLoading] = useState(false)
  const [photoUploading, setPhotoUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    setLoading(true)
    try {
      // Update Firebase Auth profile
      await updateProfile(user, { displayName })
      
      // Update Firestore user document
      await updateDoc(doc(db, 'users', user.uid), {
        displayName,
        updatedAt: new Date().toISOString(),
      })

      toast.success(t('auth.profileUpdated'))
    } catch (error: any) {
      console.error('Profile update error:', error)
      toast.error(t('errors.failedToUpdateProfile'))
    } finally {
      setLoading(false)
    }
  }

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !user) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('errors.selectImageFile'))
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('errors.imageTooLarge'))
      return
    }

    setPhotoUploading(true)
    try {
      // Create a storage reference
      const storageRef = ref(storage, `profile-photos/${user.uid}/${Date.now()}-${file.name}`)
      
      // Upload the file
      const snapshot = await uploadBytes(storageRef, file)
      
      // Get the download URL
      const photoURL = await getDownloadURL(snapshot.ref)
      
      // Update Firebase Auth profile
      await updateProfile(user, { photoURL })
      
      // Update Firestore user document
      await updateDoc(doc(db, 'users', user.uid), {
        photoURL,
        updatedAt: new Date().toISOString(),
      })

      toast.success(t('auth.profilePhotoUpdated'))
    } catch (error: any) {
      console.error('Photo upload error:', error)
      toast.error(t('errors.failedToUploadPhoto'))
    } finally {
      setPhotoUploading(false)
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handlePhotoClick = () => {
    fileInputRef.current?.click()
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <div className="min-h-screen bg-bg">
      <div className="max-w-2xl mx-auto px-lg py-2xl">
        <h1 className="text-2xl font-semibold text-text mb-xl">{t('auth.profileSettings')}</h1>
        
        <div className="bg-surface rounded-xl border border-border p-xl">
          {/* Profile Photo Section */}
          <div className="flex flex-col items-center mb-xl">
            <div className="relative mb-md">
              <div 
                className="w-24 h-24 rounded-full cursor-pointer group relative overflow-hidden"
                onClick={handlePhotoClick}
              >
                {user?.photoURL ? (
                  <img 
                    src={user.photoURL} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-primary flex items-center justify-center text-white text-xl font-medium">
                    {displayName ? getInitials(displayName) : 'U'}
                  </div>
                )}
                
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
              </div>
              
              {photoUploading && (
                <div className="absolute inset-0 rounded-full bg-black bg-opacity-50 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                </div>
              )}
            </div>
            
            <button
              onClick={handlePhotoClick}
              disabled={photoUploading}
              className="text-sm text-primary hover:text-primary-dark disabled:opacity-50"
            >
              {photoUploading ? t('common.uploading') : t('auth.changePhoto')}
            </button>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
            />
          </div>

          {/* Profile Form */}
          <form onSubmit={handleSubmit} className="space-y-lg">
            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-text mb-sm">
                {t('auth.displayName')}
              </label>
              <input
                id="displayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full px-lg py-md border border-border rounded-full bg-bg text-text placeholder-text-faint focus:outline-none focus:ring-2 focus:ring-focus transition-all"
                placeholder={t('auth.enterDisplayName')}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text mb-sm">
                {t('auth.email')}
              </label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full px-lg py-md border border-border rounded-full bg-bg-alt text-text-muted cursor-not-allowed"
              />
              <p className="text-xs text-text-faint mt-xs">
                {t('auth.emailCannotBeChanged')}
              </p>
            </div>

            <div className="flex gap-md pt-md">
              <Button
                type="submit"
                loading={loading}
                disabled={loading || !displayName.trim()}
              >
                {t('auth.saveChanges')}
              </Button>
              
              <Button
                type="button"
                variant="secondary"
                onClick={() => setDisplayName(user?.displayName || '')}
                disabled={loading}
              >
                {t('common.reset')}
              </Button>
            </div>
          </form>
        </div>

        {/* Quick Actions */}
        <div className="mt-xl">
          <h2 className="text-lg font-semibold text-text mb-lg">{t('profile.quickActions')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-md">
            <Link to="/achievements">
              <div className="bg-surface border border-border rounded-xl p-lg hover:bg-bg-alt transition-colors cursor-pointer">
                <div className="flex items-center gap-md">
                  <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🏆</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-text">{t('achievements.title')}</h3>
                    <p className="text-sm text-text-muted">{t('profile.viewAchievements')}</p>
                  </div>
                </div>
              </div>
            </Link>
            
            <Link to="/">
              <div className="bg-surface border border-border rounded-xl p-lg hover:bg-bg-alt transition-colors cursor-pointer">
                <div className="flex items-center gap-md">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-text">{t('challenges.yourChallenges')}</h3>
                    <p className="text-sm text-text-muted">{t('profile.manageChallenges')}</p>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Theme Settings Section */}
        <div className="mt-xl">
          <ThemeSettings />
        </div>

        {/* Notification Settings Section */}
        <div className="mt-xl">
          <SimpleNotificationSettings />
        </div>
      </div>
    </div>
  )
}